import {
  Container,
  Form,
  Icons,
  Input,
  Option,
  Select,
  Tooltip,
} from "@ster/ster-toolkit";
import { Select as SelectAnt } from "antd";
import { useForm } from "antd/lib/form/Form";
import { Store } from "antd/lib/form/interface";
import classNames from "classnames";
import { useCallback } from "react";
import { useRecoilValue } from "recoil";
import { useDebouncedCallback } from "use-debounce";

import { metadataState } from "./atoms";
import { SubCampaign } from "./Compare";
import styles from "./SubCampaignForm.module.less";

const SubCampaignForm = ({
  onlyLabels,
  subCampaign,
  onChange,
  onDelete,
  canDelete,
  medium,
  isFirst,
}: {
  onlyLabels: boolean;
  subCampaign: Partial<SubCampaign>;
  onChange: (updatedCampaign: SubCampaign) => void;
  onDelete: (id: number) => void;
  canDelete: boolean;
  medium: string;
  isFirst?: boolean;
}): JSX.Element => {
  const [form] = useForm<SubCampaign>();
  const metadata = useRecoilValue(metadataState(medium));

  const handleBudgetChange = useCallback(
    (value: string) => {
      onChange({
        ...(subCampaign as SubCampaign),
        budget: Number(value),
      });
    },
    [subCampaign, onChange]
  );

  const handleGrpsChange = useCallback(
    (value: string) => {
      onChange({
        ...(subCampaign as SubCampaign),
        grps: Number(value),
      });
    },
    [subCampaign, onChange]
  );

  const debouncedBudgetChange = useDebouncedCallback(handleBudgetChange, 500);
  const debouncedGrpsChange = useDebouncedCallback(handleGrpsChange, 500);

  const handleFormChange = useCallback(
    (changedValues: Store, _: Store) => {
      if (changedValues.budget) {
        debouncedBudgetChange(changedValues.budget);
        return;
      }

      if (changedValues.grps) {
        debouncedGrpsChange(changedValues.grps);
        return;
      }

      onChange({
        ...subCampaign,
        ...(changedValues as SubCampaign),
      });
    },
    [subCampaign, debouncedBudgetChange, debouncedGrpsChange, onChange]
  );

  return (
    <Container
      className={classNames(styles.subCampaign, {
        [styles.noStyle]: onlyLabels,
        [styles.first]: isFirst,
      })}
    >
      {canDelete && (
        <Tooltip title="Verwijder dit tijdvak">
          <button
            className={styles.delete}
            type="button"
            onClick={(): void => onDelete(subCampaign.id ?? 0)}
          >
            <Icons.DeleteIcon />
          </button>
        </Tooltip>
      )}
      <Form
        form={form}
        layout="horizontal"
        className={classNames("fixedWidthLabels", styles.form, {
          [styles.hideLabels]: !onlyLabels,
          [styles.hideInput]: onlyLabels,
        })}
        onValuesChange={handleFormChange}
        labelAlign="right"
      >
        <Form.Item
          name="_package"
          label="Tijdvak"
          helpText={
            <>
              Maak een keuze voor het tijdvak waarin je jouw campagne wilt
              inzetten. Dit tijdvak kan per exploitant verschillen. Staat jouw
              tijdvak er niet tussen? Kies dan voor het tijdvak dat het beste
              aansluit. Kleine afwijkingen in het tijdvak, zorgen voor een
              minimaal verschil in de prognose.
            </>
          }
        >
          <Select placeholder="Kies een tijdvak">
            {Array.from(new Set(metadata.packages.map((p) => p.fullWeek))).map(
              (fullWeek) => (
                <SelectAnt.OptGroup
                  label={fullWeek ? "Maandag - Zondag" : "Maandag - Vrijdag"}
                  key={fullWeek ? 1 : 0}
                >
                  {metadata.packages
                    .filter((s) => s.fullWeek === fullWeek)
                    .map((p) => (
                      <Option value={p.id} title={p.helpText} key={p.id}>
                        {p.label}
                      </Option>
                    ))}
                </SelectAnt.OptGroup>
              )
            )}
          </Select>
        </Form.Item>
        <Tooltip
          title={
            medium === "radio"
              ? "Invoer op basis van het (nieuwe) NMO Luisteronderzoek"
              : ""
          }
          overlayClassName={classNames("tooltip", styles.tooltip)}
          trigger={["hover"]}
        >
          <Form.Item
            name="grps"
            rules={[
              {
                validator: (_, value): Promise<void> =>
                  value >= 10 && value <= 1000
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(`Dient tussen de 10 en 1.000 te liggen`)
                      ),
              },
            ]}
            label="Aantal GRP's"
            helpText={
              <>
                Gross Rating Point is een eenheid die wordt gebruikt om het
                bereik van een reclamecampagne uit te drukken. 1 GRP = 1% bereik
                in de doelgroep.
              </>
            }
          >
            <Input.Decimal />
          </Form.Item>
        </Tooltip>
        <Form.OptionalItem
          name="budget"
          label="Factuurbedrag"
          helpText={
            <>
              Het netto bedrag wat je daadwerkelijk betaalt voor de campagne,
              inclusief alle kortingen en toeslagen. Het invullen van je budget
              is optioneel. Alleen dan kan de Campagnevergelijker ook de kosten
              per GRP op de gewenste communicatiedoelgroep uitrekenen.
            </>
          }
        >
          <Input.Currency />
        </Form.OptionalItem>
      </Form>
    </Container>
  );
};

export default SubCampaignForm;
